import bugsnag from "@bugsnag/js";
import classNames from "classnames";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import ButtonSmily from "@Components/atoms/ButtonSmily";
import { ReactComponent as IconArrowRight } from "@Icons/16x16/arrow-right.svg";
import { ReactComponent as IconArrowLeft } from "@Icons/arrow-left.svg";
import { Section, Tile } from "@Types/pagify";
import { getLink } from "@Utils/pagify";
import "./style.scss";

export const HomeCollections: FunctionComponent<{
  section: Section;
  tiles: Tile[];
}> = ({ section, tiles }) => {
  const intl = useIntl();
  const localeSlug = intl.locale;

  const [disabledLeft, setDisabledLeft] = useState<boolean>(true);
  const [disabledRight, setDisabledRight] = useState<boolean>(false);
  const scrollWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = scrollWrapperRef.current;
    if (element) {
      element.addEventListener("scroll", () => handleDisabled(element));
      return () => {
        element.removeEventListener("scroll", () => handleDisabled(element));
      };
    }
  }, [scrollWrapperRef.current]);

  const handleDisabled = (element: HTMLDivElement) => {
    setDisabledLeft(element.scrollLeft === 0);
    setDisabledRight(element.scrollLeft === element.scrollWidth - element.clientWidth);
  };

  const handleScroll = (direction: string) => {
    if (scrollWrapperRef.current) {
      scrollWrapperRef.current.scrollBy({
        left: direction === "left" ? -350 : 350,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="HomeCollections">
      {!!section.title && <div className="HomeCollections-title">{section.title}</div>}
      <div className="HomeCollections-subtitle">
        {section.subtitle.length > 0 &&
          section.subtitle.map((textBlock) => {
            return textBlock.navigationLink ? (
              <a
                className="HomeCollections-subtitle-link"
                href={getLink(textBlock.navigationLink, localeSlug)}
                key={textBlock.text}
              >
                {textBlock.text}
              </a>
            ) : (
              <span className="HomeCollections-subtitle-text" key={textBlock.text}>
                {textBlock.text}
              </span>
            );
          })}
      </div>
      {tiles.length > 3 && (
        <div
          className={classNames("HomeCollections-left", {
            "HomeCollections-left--disabled": disabledLeft,
          })}
        >
          <ButtonSmily
            icon={<IconArrowLeft />}
            iconPosition="alone"
            disabled={disabledLeft}
            onClick={() => handleScroll("left")}
            size="s"
            type="secondary"
          />
        </div>
      )}
      <div className="HomeCollections-list-wrapper">
        <div className="HomeCollections-list" ref={scrollWrapperRef}>
          {tiles.map((tile) => {
            const tileTitle = tile.textBlock1 && tile.textBlock1[0].text;
            const tileImage = tile.images.length > 0 && tile.images[0].desktop;

            if (!tileImage) {
              bugsnag.notify(new Error(`image for tile ${tile.slug} not found in HomeCollections`));
            }

            return (
              <div
                className="HomeCollections-item"
                key={tile.slug}
                style={{
                  backgroundColor: tile.backgroundColor ? tile.backgroundColor?.hex : "#EBF3FF",
                }}
              >
                {!!tileImage && (
                  <a
                    className="HomeCollections-item-image-wrapper"
                    href={getLink(tile.navigationLink, localeSlug)}
                  >
                    <img
                      alt={`${tileTitle ? tileTitle : ""} Cheerz`}
                      className="HomeCollections-item-image"
                      src={tileImage["1x"]}
                      srcSet={`${tileImage["1x"]} 1x, ${tileImage["2x"]} 2x`}
                    />
                  </a>
                )}
                <h2 className="HomeCollections-item-title">
                  <a
                    className="HomeCollections-item-title-link"
                    href={getLink(tile.navigationLink, localeSlug)}
                  >
                    {tileTitle}
                  </a>
                </h2>
                {!!tile.cta && (
                  <ButtonSmily
                    href={getLink(tile.cta.navigationLink, localeSlug)}
                    icon={<IconArrowRight />}
                    iconPosition="right"
                    size="s"
                    type="secondary"
                  >
                    {tile.cta.text}
                  </ButtonSmily>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {tiles.length > 3 && (
        <div
          className={classNames("HomeCollections-right", {
            "HomeCollections-right--disabled": disabledRight,
          })}
        >
          <ButtonSmily
            icon={<IconArrowRight />}
            iconPosition="alone"
            disabled={disabledRight}
            onClick={() => handleScroll("right")}
            size="s"
            type="secondary"
          />
        </div>
      )}
    </div>
  );
};
